import * as React from 'react';
import { connect } from 'react-redux';
import { provideLocalizationService, registerForLocalization } from '@progress/kendo-react-intl';
import { Button } from '@progress/kendo-react-buttons';
import { Skeleton } from '@progress/kendo-react-indicators';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { QRCodeSVG } from 'qrcode.react';
import styled from 'styled-components';
import { StackLayout, Text, WarningMessage } from '../../../ui';
import { EmptyFormatter, ValueItem } from '../../shared/components';
import { GENERAL_COMPETENCY_EVENT_STATUSES } from '../../../resources/generalCompetencyEventStatus';
import {
  loadSessionQr,
  generateSessionQr,
  rotateSessionQr,
  disableSessionQr
} from '../actions';
import {
  QR_BROADCAST_CHANNEL,
  QR_BROADCAST_ROTATE,
  QR_BROADCAST_DISABLE,
  QR_BROADCAST_CLOSE,
  CountdownText,
  computeCountdown
} from './session-qr-shared';

const TokenCode = styled.div`
  margin-top: 16px;
  font-size: 28px;
  font-weight: bold;
  letter-spacing: 4px;
  font-family: monospace;
`;

class GeneralCompetencyEventSessionQrDialog extends React.PureComponent {

  constructor() {
    super();
    this.state = { showCode: false, isCloseConfirmVisible: false, countdown: null };
    this.handleGenerate = this.handleGenerate.bind(this);
    this.handleRotate = this.handleRotate.bind(this);
    this.handleDisable = this.handleDisable.bind(this);
    this.handleOpenPopup = this.handleOpenPopup.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleToggleCode = this.handleToggleCode.bind(this);
    this.handleCloseRequest = this.handleCloseRequest.bind(this);
    this.handleCloseConfirm = this.handleCloseConfirm.bind(this);
    this.handleCloseCancel = this.handleCloseCancel.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    this.autoRotationTimer = null;
    this.countdownInterval = null;
    this.isAutoRotating = false;
    this.popupWindow = null;
    this.broadcastChannel = null;
  }

  componentDidMount() {
    if (this.props.eventId && this.props.sessionId) {
      this.props.loadSessionQr({
        eventId: this.props.eventId,
        sessionId: this.props.sessionId
      });
    }
    document.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.broadcastChannel = new BroadcastChannel(QR_BROADCAST_CHANNEL);
  }

  componentDidUpdate(prevProps) {
    if (this.props.qr && this.props.qr !== prevProps.qr) {
      this.startAutoRotation();
      this.broadcastChannel?.postMessage({ type: QR_BROADCAST_ROTATE });
    }
    if (!this.props.qr && prevProps.qr) {
      this.stopAutoRotation();
      this.broadcastChannel?.postMessage({ type: QR_BROADCAST_DISABLE });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.stopAutoRotation();
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage({ type: QR_BROADCAST_CLOSE });
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
    if (this.popupWindow) {
      this.popupWindow.close();
      this.popupWindow = null;
    }
  }

  render() {
    const { loading, qr, canWrite } = this.props;
    const localizationService = provideLocalizationService(this);
    return (
      <>
        <Dialog
          title={localizationService.toLanguageString('generalCompetencyEventSessionQr.dialogTitle')}
          onClose={this.handleCloseRequest}
        >
          <StackLayout align='center' valign='center' rowGap='0px' style={{ flex: 1, padding: '24px 16px' }}>
            {this.props.eventStatus && this.props.eventStatus !== GENERAL_COMPETENCY_EVENT_STATUSES.PUBLISHED &&
              <WarningMessage>{localizationService.toLanguageString('generalCompetencyEventSessionQr.eventNotPublishedWarning')}</WarningMessage>
            }
            {loading ? (
              <>
                <Skeleton shape='rectangle' style={{ width: 256, height: 256 }} />
                {this.state.showCode &&
                  <Skeleton shape='text' style={{ width: 200, height: 32, marginTop: 16 }} />
                }
                <Skeleton shape='text' style={{ width: 150, height: 30, marginTop: 16 }} />
                <StackLayout rowGap='4px' style={{ marginTop: '16px', padding: '12px 16px', width: '100%' }}>
                  <ValueItem
                    label={localizationService.toLanguageString('generalCompetencyEventSessionQr.status')}
                    labelWidth='120px'
                    loading
                  />
                </StackLayout>
              </>
            ) : qr ? (
              <>
                <QRCodeSVG value={qr.url} size={256} />
                {this.state.showCode &&
                  <TokenCode>{qr.token}</TokenCode>
                }
                {this.state.countdown &&
                  <CountdownText $expired={this.state.countdown.expired}>
                    {this.state.countdown.expired
                      ? localizationService.toLanguageString('generalCompetencyEventSessionQr.expired')
                      : `${localizationService.toLanguageString('generalCompetencyEventSessionQr.expiresIn')} ${this.state.countdown.text}`
                    }
                  </CountdownText>
                }
                <StackLayout rowGap='4px' style={{ marginTop: '16px', padding: '12px 16px', width: '100%' }}>
                  <ValueItem
                    label={localizationService.toLanguageString('generalCompetencyEventSessionQr.status')}
                    labelWidth='120px'
                  >
                    {EmptyFormatter(qr.status)}
                  </ValueItem>
                </StackLayout>
              </>
            ) : (
              <Text>{localizationService.toLanguageString('generalCompetencyEventSessionQr.noActiveQr')}</Text>
            )}
          </StackLayout>
          <DialogActionsBar>
            {canWrite &&
              <>
                {!qr &&
                  <Button themeColor='primary' disabled={loading} onClick={this.handleGenerate}>
                    {localizationService.toLanguageString('generalCompetencyEventSessionQr.generate')}
                  </Button>
                }
                {qr &&
                  <>
                    <Button themeColor='primary' disabled={loading} onClick={this.handleOpenPopup}>
                      {localizationService.toLanguageString('generalCompetencyEventSessionQr.openPopup')}
                    </Button>
                    <Button disabled={loading} onClick={this.handleToggleCode}>
                      {localizationService.toLanguageString(this.state.showCode
                        ? 'generalCompetencyEventSessionQr.hideCode'
                        : 'generalCompetencyEventSessionQr.showCode'
                      )}
                    </Button>
                    <Button disabled={loading} onClick={this.handleRotate}>
                      {localizationService.toLanguageString('generalCompetencyEventSessionQr.rotate')}
                    </Button>
                    <Button disabled={loading} onClick={this.handleDisable}>
                      {localizationService.toLanguageString('generalCompetencyEventSessionQr.disable')}
                    </Button>
                  </>
                }
              </>
            }
            <Button onClick={this.handleCloseRequest}>
              {localizationService.toLanguageString('generalCompetencyEventSessionQr.close')}
            </Button>
          </DialogActionsBar>
        </Dialog>
        {this.state.isCloseConfirmVisible &&
          <Dialog
            title={localizationService.toLanguageString('generalCompetencyEventSessionQr.closeConfirmTitle')}
            onClose={this.handleCloseCancel}
            width={400}
          >
            <Text>{localizationService.toLanguageString('generalCompetencyEventSessionQr.closeConfirmMessage')}</Text>
            <DialogActionsBar>
              <Button onClick={this.handleCloseCancel}>
                {localizationService.toLanguageString('generalCompetencyEventSessionQr.closeConfirmCancel')}
              </Button>
              <Button themeColor='primary' onClick={this.handleCloseConfirm}>
                {localizationService.toLanguageString('generalCompetencyEventSessionQr.closeConfirmOk')}
              </Button>
            </DialogActionsBar>
          </Dialog>
        }
      </>
    );
  }

  startAutoRotation() {
    this.isAutoRotating = false;
    this.stopAutoRotation();
    this.updateCountdown();
    if (!this.isAutoRotating) {
      this.countdownInterval = setInterval(() => this.updateCountdown(), 1000);
    }
  }

  stopAutoRotation() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    if (this.autoRotationTimer) {
      clearTimeout(this.autoRotationTimer);
      this.autoRotationTimer = null;
    }
    this.setState({ countdown: null });
  }

  updateCountdown() {
    if (this.isAutoRotating) {
      return;
    }
    const countdown = computeCountdown(this.props.qr?.validUntil);
    if (!countdown) {
      return;
    }
    this.setState({ countdown });
    if (countdown.expired) {
      this.triggerAutoRotate();
    }
  }

  triggerAutoRotate() {
    if (this.isAutoRotating) {
      return;
    }
    this.isAutoRotating = true;
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    const { qr, eventId, sessionId } = this.props;
    if (qr) {
      this.props.rotateSessionQr({ eventId, sessionId, qrId: qr.id });
    }
  }

  handleGenerate() {
    this.props.generateSessionQr({
      eventId: this.props.eventId,
      sessionId: this.props.sessionId
    });
  }

  handleRotate() {
    this.props.rotateSessionQr({
      eventId: this.props.eventId,
      sessionId: this.props.sessionId,
      qrId: this.props.qr.id
    });
  }

  handleDisable() {
    this.stopAutoRotation();
    this.props.disableSessionQr({
      eventId: this.props.eventId,
      sessionId: this.props.sessionId,
      qrId: this.props.qr.id
    });
  }

  handleOpenPopup() {
    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.focus();
      return;
    }
    const url = `/general_competency_events/${this.props.eventId}/sessions/${this.props.sessionId}/qr_display`;
    const width = Math.round(screen.width * 0.5);
    const height = Math.round(screen.height * 0.75);
    this.popupWindow = window.open(url, 'qr-display', `width=${width},height=${height},scrollbars=no,resizable=yes`);
  }

  handleToggleCode() {
    this.setState(prevState => ({ showCode: !prevState.showCode }));
  }

  handleCloseRequest() {
    if (this.props.qr) {
      this.setState({ isCloseConfirmVisible: true });
    } else {
      this.props.onClose();
    }
  }

  handleCloseConfirm() {
    this.setState({ isCloseConfirmVisible: false });
    this.broadcastChannel?.postMessage({ type: QR_BROADCAST_CLOSE });
    this.popupWindow?.close();
    this.popupWindow = null;
    this.props.onClose();
  }

  handleCloseCancel() {
    this.setState({ isCloseConfirmVisible: false });
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' && this.state.isCloseConfirmVisible) {
      e.preventDefault();
      e.stopPropagation();
      this.handleCloseCancel();
    }
  }

  handleBeforeUnload() {
    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.close();
    }
  }
}

registerForLocalization(GeneralCompetencyEventSessionQrDialog);

const mapStateToProps = state => ({
  loading: state.generalCompetencyEventSessionQrPanel.loading,
  qr: state.generalCompetencyEventSessionQrPanel.qr
});

const mapDispatchToProps = dispatch => ({
  loadSessionQr: (payload) => dispatch(loadSessionQr(payload)),
  generateSessionQr: (payload) => dispatch(generateSessionQr(payload)),
  rotateSessionQr: (payload) => dispatch(rotateSessionQr(payload)),
  disableSessionQr: (payload) => dispatch(disableSessionQr(payload))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GeneralCompetencyEventSessionQrDialog);
